// src/pages/AzioneAccount.js — PAGINA DEI LINK NELLE EMAIL DI NEXIORA (W447, soluzione A2)
// ═══════════════════════════════════════════════════════════════════════════
// Si apre da app.nexiora.it/?azione=…&codice=… (src/index.js), con o senza accesso:
//  • reimposta-password: «Password dimenticata?» → email Nexiora → nuova password + conferma;
//  • imposta-password:   titolare creato dal Pannello Owner → sceglie la sua prima password;
//  • conferma-email:     email di conferma dopo la registrazione dell'azienda.
// Il codice (oobCode) è di Firebase: lo controlla e lo consuma Firebase (verifyPasswordResetCode,
// confirmPasswordReset, applyActionCode). Scaduto o già usato → messaggio chiaro e nuovo invio.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from "react";
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import NuovaPassword from "../components/NuovaPassword";
import { erroreNuovaPassword, mascheraEmail } from "../utils/regolePassword";

export const AZIONI_ACCOUNT = ["reimposta-password", "imposta-password", "conferma-email"];
export const azioneDaUrl = () => {
  const q = new URLSearchParams(window.location.search);
  const a = q.get("azione");
  return AZIONI_ACCOUNT.includes(a) ? { azione: a, codice: q.get("codice") || "" } : null;
};
const VIOLA = "#7c3aed";

/** Torna alla pagina di accesso (con l'email già scritta) o all'app. */
function vaiAllaApp(email) {
  try { if (email) sessionStorage.setItem("nexiora_email_accesso", email); } catch { /* */ }
  window.location.replace("/");
}

function Logo() {
  return (
    <svg width="54" height="54" viewBox="0 0 72 72" aria-hidden="true" style={{ marginBottom: 10 }}>
      <defs><linearGradient id="lga" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#7c3aed"/></linearGradient></defs>
      <polygon points="36,2 70,36 36,70 2,36" fill="url(#lga)"/>
      <line x1="22" y1="24" x2="22" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
      <line x1="50" y1="24" x2="50" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
      <line x1="22" y1="24" x2="50" y2="48" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
    </svg>
  );
}

/** Link scaduto o non valido: si chiede un nuovo link senza tornare indietro. */
function NuovoLink({ titolo, testo }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [stato, setStato] = useState("");
  const invia = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setStato("Scrivi un indirizzo email valido."); return; }
    setStato("invio");
    try { await resetPassword(email.trim()); setStato("inviata"); }
    catch (err) { setStato(err?.message && !/internal/i.test(err.message) ? err.message : "Invio non riuscito: riprova tra qualche minuto."); }
  };
  return (
    <div data-test="link-non-valido">
      <h1 style={titoloStile}>{titolo}</h1>
      <p style={testoStile}>{testo}</p>
      {stato === "inviata" ? (
        <div role="status" style={{ ...boxOk }}>Se l'indirizzo è registrato su Nexiora, tra poco ricevi un'email con un nuovo link. Guarda anche nello spam.</div>
      ) : (
        <form onSubmit={invia}>
          <label className="form-label" htmlFor="email-nuovo-link">La tua email</label>
          <input id="email-nuovo-link" className="input" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nome@azienda.it" />
          {stato && stato !== "invio" && <div role="alert" style={{ fontSize: 12.5, color: "#b91c1c", marginTop: 6 }}>{stato}</div>}
          <button type="submit" disabled={stato === "invio"} style={{ ...bottone, marginTop: 12 }}>{stato === "invio" ? "Invio…" : "Mandami un nuovo link"}</button>
        </form>
      )}
      <button type="button" onClick={() => vaiAllaApp("")} style={linkStile}>Torna all'accesso</button>
    </div>
  );
}

function Password({ codice, nuovo }) {
  const [fase, setFase] = useState("controllo"); // controllo | modulo | salvo | fatto | scaduto | non-valido
  const [email, setEmail] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [errore, setErrore] = useState("");
  useEffect(() => {
    if (!codice) { setFase("non-valido"); return; }
    verifyPasswordResetCode(auth, codice)
      .then(e => { setEmail(e || ""); setFase("modulo"); })
      .catch(err => setFase(err?.code === "auth/expired-action-code" ? "scaduto" : "non-valido"));
  }, [codice]);
  const salva = async (e) => {
    e.preventDefault();
    const err = erroreNuovaPassword(p1, email) || (p1 !== p2 ? "Le due password non coincidono." : null);
    if (err) { setErrore(err); return; }
    setFase("salvo"); setErrore("");
    try {
      await confirmPasswordReset(auth, codice, p1);
      setFase("fatto");
    } catch (er) {
      if (er?.code === "auth/expired-action-code") setFase("scaduto");
      else if (er?.code === "auth/invalid-action-code") setFase("non-valido");
      else { setErrore(er?.code === "auth/weak-password" ? "Password troppo debole: allungala." : er?.code === "auth/network-request-failed" ? "Connessione assente: riprova." : "Non è stato possibile salvare: riprova."); setFase("modulo"); }
    }
  };
  if (fase === "controllo") return <p style={testoStile}>Controllo il link…</p>;
  if (fase === "scaduto") return <NuovoLink titolo="Il link è scaduto" testo="Per sicurezza i link per la password valgono poco tempo. Scrivi la tua email: te ne mandiamo uno nuovo." />;
  if (fase === "non-valido") return <NuovoLink titolo="Link non valido o già usato" testo="Ogni link si usa una volta sola. Se la password l'hai già cambiata, torna all'accesso; altrimenti chiedi un nuovo link." />;
  if (fase === "fatto") return (
    <div data-test="password-aggiornata">
      <div style={{ fontSize: 34, marginBottom: 6 }}>✅</div>
      <h1 style={titoloStile}>{nuovo ? "Password impostata" : "Password aggiornata"}</h1>
      <p style={testoStile}>Da adesso entri con la nuova password. Per sicurezza, gli altri dispositivi collegati al tuo account ti chiederanno di accedere di nuovo.</p>
      <button type="button" onClick={async () => { try { await signOut(auth); } catch { /* */ } vaiAllaApp(email); }} style={bottone}>Vai all'accesso</button>
    </div>
  );
  return (
    <form onSubmit={salva} data-test="modulo-password">
      <h1 style={titoloStile}>{nuovo ? "Imposta la tua password" : "Scegli una nuova password"}</h1>
      <p style={testoStile}>Per l'account <strong>{mascheraEmail(email)}</strong>.</p>
      <NuovaPassword valore={p1} conferma={p2} onValore={v => { setP1(v); setErrore(""); }} onConferma={v => { setP2(v); setErrore(""); }} email={email} autoFocus />
      {errore && <div role="alert" style={{ fontSize: 12.5, color: "#b91c1c", marginBottom: 10 }}>{errore}</div>}
      <button type="submit" disabled={fase === "salvo"} style={bottone}>{fase === "salvo" ? "Salvo…" : (nuovo ? "Imposta la password" : "Salva la nuova password")}</button>
      <button type="button" onClick={() => vaiAllaApp("")} style={linkStile}>Annulla e torna all'accesso</button>
    </form>
  );
}

function ConfermaEmail({ codice }) {
  const { currentUser, aggiornaVerificaEmail } = useAuth();
  const [fase, setFase] = useState("controllo");
  const usato = useRef(false); // il codice si usa una volta sola: una sola chiamata, anche se la pagina si ridisegna
  useEffect(() => {
    if (usato.current) return;
    usato.current = true;
    if (!codice) { setFase("non-valido"); return; }
    applyActionCode(auth, codice)
      .then(async () => { setFase("fatto"); try { if (currentUser) await aggiornaVerificaEmail(); } catch { /* */ } })
      .catch(err => setFase(err?.code === "auth/expired-action-code" ? "scaduto" : "non-valido"));
  }, [codice, currentUser, aggiornaVerificaEmail]);
  if (fase === "controllo") return <p style={testoStile}>Confermo la tua email…</p>;
  if (fase === "fatto") return (
    <div data-test="email-confermata">
      <div style={{ fontSize: 34, marginBottom: 6 }}>✅</div>
      <h1 style={titoloStile}>Email confermata</h1>
      <p style={testoStile}>Grazie! Ora puoi invitare i colleghi e usare Nexiora senza limiti di tempo.</p>
      <button type="button" onClick={() => vaiAllaApp("")} style={bottone}>Apri Nexiora</button>
    </div>
  );
  return (
    <div data-test="conferma-non-valida">
      <h1 style={titoloStile}>{fase === "scaduto" ? "Il link è scaduto" : "Link non valido o già usato"}</h1>
      <p style={testoStile}>Se l'email risulta già confermata non devi fare altro. Altrimenti apri Nexiora e premi «Rimanda l'email» nella fascia in alto: ti arriva un link nuovo.</p>
      <button type="button" onClick={() => vaiAllaApp("")} style={bottone}>Apri Nexiora</button>
    </div>
  );
}

export default function AzioneAccount() {
  const a = azioneDaUrl() || { azione: "", codice: "" };
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "linear-gradient(135deg,#0f0c29,#302b63,#24243e)", overflowY: "auto", boxSizing: "border-box" }}>
      <div style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 16, padding: "28px 28px 22px", boxShadow: "0 20px 50px rgba(0,0,0,.35)", textAlign: "left" }}>
        <div style={{ textAlign: "center" }}><Logo /><div style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b", letterSpacing: "-.2px", marginBottom: 16 }}>Nexiora</div></div>
        {a.azione === "conferma-email" ? <ConfermaEmail codice={a.codice} /> : <Password codice={a.codice} nuovo={a.azione === "imposta-password"} />}
      </div>
    </div>
  );
}

const titoloStile = { fontSize: 19, fontWeight: 800, color: "#1e1b4b", margin: "0 0 6px" };
const testoStile = { fontSize: 13.5, color: "#4b5563", lineHeight: 1.55, margin: "0 0 16px" };
const bottone = { width: "100%", padding: "11px", background: VIOLA, color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" };
const linkStile = { display: "block", width: "100%", marginTop: 12, background: "none", border: "none", color: VIOLA, fontSize: 13, cursor: "pointer", fontFamily: "'Inter',sans-serif" };
const boxOk = { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#166534", lineHeight: 1.5, marginBottom: 8 };
